import { Box, Divider, Grid } from '@mui/material'
import CustomText from 'components/common/CustomText/CustomText'
import CustomDatePicker from 'components/inputs/CustomDatePicker/CustomDatePicker'
import CustomFormSelect from 'components/inputs/CustomFormSelect/CustomFormSelect'
import CustomFormTextInput from 'components/inputs/CustomFormTextInput/CustomFormTextInput'
import SearchUsers from 'components/user/SearchUsers/SearchUsers'
import { Controller, FieldError, FieldErrorsImpl, Merge, useFormContext } from 'react-hook-form'
import { colors } from 'styles/colors'
import { caseSharedProps, procedureDataType } from 'types/types'
import useGlobalStyles from 'styles/globalStyles'
import { memo, useEffect, useMemo, useState } from 'react'
import PositionDialog from 'components/user/PositionDialog/PositionDialog'
import { ProfileProvider } from 'context/ProfileContext'
import { RHFMaybeFieldError, toFieldError } from 'common/utils'



interface Props {
  shared: caseSharedProps;
}

function PatientInformation({
  shared,
}: Props) {
  const globalStyles = useGlobalStyles();
  const [open, setOpen] = useState<boolean>(false);

  const {
    genders,
    raceList,
    insuranceList,
    insuranceStatusList,
    userHospitals,
    refetchHospitals,
  } = shared;

  const {
    register,
    control,
    setValue,
    formState: { errors },
  } = useFormContext<procedureDataType>()

  const hospitalsWithAction = useMemo(() => [
    ...(userHospitals || []),
    { value: '__add_hospital__', label: 'Add Institution' },
  ], [userHospitals]);

  return (
    <ProfileProvider>
      <Box
        className={globalStyles.sectionContainer}
      >
        <Box className={globalStyles.sectionHeader}>
          <CustomText
            text="Patient Information"
            color={colors.primary}
            fontWeight={600}
          />
        </Box>

        <Divider />

        <Grid container p={3} spacing={1.5}>

          {/* Patient last name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="patient_last_name"
              label="Patient Last Name"
              required
              placeholder="Patient last name"
              register={register}
              error={toFieldError(errors.patient_last_name as RHFMaybeFieldError)}
            />
          </Grid>

          {/* Patient first name */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="patient_first_name"
              label="Patient First Name"
              required
              placeholder="Patient first name"
              register={register}
              error={toFieldError(errors.patient_first_name as RHFMaybeFieldError)}
            />
          </Grid>

          {/* Gender */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="gender"
              label="Gender"
              required={true}
              placeholder="Select gender"
              error={toFieldError(errors.gender as RHFMaybeFieldError)}
              options={genders || []}
            />
          </Grid>

          {/* Date of birth */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomDatePicker
              name="date_of_birth"
              label="Date of Birth"
              required={false}
              control={control}
              placeholder="Date of birth"
            />
          </Grid>

          {/* Age */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="age"
              label="Age"
              required={false}
              placeholder="Age"
              register={register}
              error={toFieldError(errors.age as RHFMaybeFieldError)}
            />
          </Grid>

          {/* MRN */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormTextInput
              name="mrn"
              label="Medical Record Number"
              required={false}
              placeholder="Medical record number"
              register={register}
              error={toFieldError(errors.mrn as RHFMaybeFieldError)}
            />
          </Grid>

          {/* Race */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="race"
              label="Race"
              required={false}
              placeholder="Select race"
              error={toFieldError(errors.race as RHFMaybeFieldError)}
              options={raceList || []}
            />
          </Grid>

          {/* Insurance */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="insurance"
              label="Insurance"
              required={false}
              placeholder="Select insurance"
              error={toFieldError(errors.insurance as RHFMaybeFieldError)}
              options={insuranceList || []}
            />
          </Grid>

          {/* Insurance status */}
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              name="insurance_status"
              label="Insurance Status"
              required={false}
              placeholder="Select insurance status"
              error={toFieldError(errors.insurance_status as RHFMaybeFieldError)}
              options={insuranceStatusList || []}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <CustomFormSelect
              key="hospital-select"
              name="hospital_id"
              label="Institution"
              required
              placeholder="Select institution"
              error={toFieldError(errors.hospital_id as RHFMaybeFieldError)}
              options={hospitalsWithAction}
              onChange={(e) => {
                const value = e.target.value;

                if (value === "__add_hospital__") {
                  setOpen(true)
                  return;
                }

                setValue("hospital_id", Number(value), {
                  shouldValidate: false,
                  shouldDirty: true
                });
              }}
            />
          </Grid>

          {/* Primary surgeons */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="primary_surgeons"
              control={control}
              render={({ field }) => (
                <SearchUsers
                  title="Primary Surgeon(s)"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Primary surgeon(s)"
                  label='Primary Surgeon(s)'
                  required={false}
                  note="(if not you)"
                />
              )}
            />
          </Grid>

          {/* Co surgeons */}
          <Grid size={{ xs: 12, md: 6 }}>
            <Controller
              name="co_surgeons"
              control={control}
              render={({ field }) => (
                <SearchUsers
                  title="Co-Surgeon(s)"
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Co-surgeon(s)"
                  label="Co-Surgeon(s)"
                  required={false}
                />
              )}
            />
          </Grid>


        </Grid>

        {/* <CaseProcedure
          procedureFields={procedureFields}
          append={append}
          remove={remove}
          register={register}
          errors={errors}
        /> */}


      </Box>


      <PositionDialog
        open={open}
        setOpen={setOpen}
        entity={'position'}
        refetchHospitals={refetchHospitals}
      />
    </ProfileProvider>
  )
}

export default memo(PatientInformation);
